import { Box, Card, Typography, Button, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getPendingApprovalsSummary, formatCurrency } from "../../../../../utils/mockWalletData";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import HandshakeIcon from "@mui/icons-material/Handshake";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useState, useEffect } from "react";
import AppService from "../../../../../Configurations/Appservice";

/**
 * PendingApprovalsSimplified - Ultra-compact holistic summary for My Wallet dashboard
 *
 * Shows:
 * - Total pending amount (main focus)
 * - 3 inline sections with key metrics
 * - Details button for deep dive
 * - Total will tally correctly
 */
const PendingApprovalsSimplified = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(getPendingApprovalsSummary());
  const [loading, setLoading] = useState(false);

  // Fetch real data from finance overview API.
  // Only service-partner and channel-partner sub-sections are needed; calling
  // them in parallel avoids the slow monolithic /overview aggregation.
  useEffect(() => {
    const fetchFinanceOverview = async () => {
      setLoading(true);
      try {
        const [partner, channel] = await Promise.all([
          AppService.get("v1/admin/finance/overview/service-partner").catch(() => null),
          AppService.get("v1/admin/finance/overview/channel-partner").catch(() => null)
        ]);

        const partnerPending = partner?.pendingServicePartnerPayments || 0;
        const partnerPendingCount = partner?.pendingServicePartnerCount || 0;
        const channelPending = channel?.pendingChannelPartnerCommissions || 0;
        const channelPendingCount = channel?.pendingChannelPartnerCount || 0;

        const realSummary = {
          customerTopups: {
            total: 0, // TODO: Get from payment approvals API when ready
            count: 0,
            items: []
          },
          inTransit: {
            total: partnerPending,
            count: partnerPendingCount,
            priority: 'MEDIUM',
            alerts: { normal: 0, followUp: 0, overdue: 0 }
          },
          channelCommissions: {
            total: channelPending,
            count: channelPendingCount
          },
          totals: {
            totalAwaitingAction: partnerPendingCount + channelPendingCount,
            totalAmount: 0 - partnerPending - channelPending,
            urgentActions: 0
          }
        };
        setSummary(realSummary);
      } catch (error) {
        console.error("Error fetching finance overview:", error);
        // Fall back to mock data on error
        setSummary(getPendingApprovalsSummary());
      } finally {
        setLoading(false);
      }
    };

    fetchFinanceOverview();
  }, []);

  const handleViewDetails = () => {
    navigate("/wallet/pending-approvals");
  };

  // Verify total tally
  const calculatedTotal =
    summary.customerTopups.total +
    summary.inTransit.total +
    summary.channelCommissions.total;

  return (
    <Card
      sx={{
        p: 1,
        background: "linear-gradient(135deg, #ffffff 0%, #f0fffe 100%)",
        border: "1px solid #b2dfdb",
        borderRadius: 1.5,
        boxShadow: "0 2px 8px rgba(0, 137, 123, 0.12)",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 4px 16px rgba(0, 137, 123, 0.18)",
          borderColor: "#00897b",
        },
      }}
    >
      {/* Header Section - Compact Single Line */}
      <Box sx={{ mb: 0.65, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#00897b", fontSize: "1rem", lineHeight: 1 }}>
            Pending Approvals ({summary.totals.totalAwaitingAction} items)
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.02, fontSize: "0.72rem", fontWeight: 600, lineHeight: 1 }}>
            Net Balance
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.1rem",
              color: calculatedTotal >= 0 ? "#00897b" : "#d32f2f",
              lineHeight: 1,
            }}
          >
            {formatCurrency(summary.totals.totalAmount)}
          </Typography>
        </Box>
      </Box>

      {/* Breakdown Stats - 3 Column Layout */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 0.5,
          mb: 0.8,
        }}
      >
        {/* 1. Customer Top-ups - MONEY IN */}
        <Box
          sx={{
            p: 0.6,
            bgcolor: "#e0f2f1",
            borderRadius: 1,
            border: "1px solid #b2dfdb",
            transition: "all 0.3s ease",
            cursor: "pointer",
            "&:hover": {
              bgcolor: "#b2dfdb",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(0, 137, 123, 0.2)",
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                p: 0.3,
                bgcolor: "#ffffff",
                borderRadius: 0.6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #b2dfdb",
                minWidth: 20,
                height: 20,
                flexShrink: 0,
              }}
            >
              <AccountBalanceWalletIcon sx={{ fontSize: 12, color: "#00897b" }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.01, fontSize: "0.72rem", lineHeight: 1 }}>
                From Customers
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: "#00897b", fontWeight: 600, fontSize: "0.72rem", lineHeight: 1 }}>
                Incoming
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#00695c", fontSize: "1rem", lineHeight: 1 }}>
              {summary.customerTopups.count}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1 }}>
              {formatCurrency(summary.customerTopups.total)}
            </Typography>
          </Box>
        </Box>

        {/* 2. Partner Payments - MONEY OUT */}
        <Box
          sx={{
            p: 0.6,
            bgcolor: summary.inTransit.priority === "URGENT" ? "#ffebee" : "#e3f2fd",
            borderRadius: 1,
            border: `1px solid ${summary.inTransit.priority === "URGENT" ? "#ffcdd2" : "#bbdefb"}`,
            transition: "all 0.3s ease",
            cursor: "pointer",
            "&:hover": {
              bgcolor: summary.inTransit.priority === "URGENT" ? "#ffcdd2" : "#bbdefb",
              transform: "translateY(-2px)",
              boxShadow: `0 4px 12px rgba(${summary.inTransit.priority === "URGENT" ? "211, 47, 47" : "25, 118, 210"}, 0.2)`,
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                p: 0.3,
                bgcolor: "#ffffff",
                borderRadius: 0.6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${summary.inTransit.priority === "URGENT" ? "#ffcdd2" : "#bbdefb"}`,
                minWidth: 20,
                height: 20,
                flexShrink: 0,
              }}
            >
              <HourglassEmptyIcon
                sx={{
                  fontSize: 12,
                  color: summary.inTransit.priority === "URGENT" ? "#d32f2f" : "#1976d2",
                }}
              />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.01, fontSize: "0.72rem", lineHeight: 1 }}>
                To Service Partners
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: summary.inTransit.priority === "URGENT" ? "#d32f2f" : "#1976d2", fontWeight: 600, fontSize: "0.72rem", lineHeight: 1 }}>
                Outgoing
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0, display: "flex", alignItems: "center", gap: 0.2 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: summary.inTransit.priority === "URGENT" ? "#c62828" : "#1565c0", fontSize: "1rem", lineHeight: 1 }}>
                {summary.inTransit.count}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1 }}>
                {formatCurrency(summary.inTransit.total)}
              </Typography>
            </Box>
            {summary.inTransit.alerts.overdue > 0 && (
              <Chip
                label="⚠"
                size="small"
                color="error"
                sx={{ height: 12, fontSize: "0.5rem", padding: "0 1px" }}
              />
            )}
          </Box>
        </Box>

        {/* 3. Channel Commissions - MONEY OUT */}
        <Box
          sx={{
            p: 0.6,
            bgcolor: "#fff3e0",
            borderRadius: 1,
            border: "1px solid #ffe0b2",
            transition: "all 0.3s ease",
            cursor: "pointer",
            "&:hover": {
              bgcolor: "#ffe0b2",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(230, 124, 15, 0.2)",
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                p: 0.3,
                bgcolor: "#ffffff",
                borderRadius: 0.6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #ffe0b2",
                minWidth: 20,
                height: 20,
                flexShrink: 0,
              }}
            >
              <HandshakeIcon sx={{ fontSize: 12, color: "#e67c3b" }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.01, fontSize: "0.72rem", lineHeight: 1 }}>
                To Channel Partners
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: "#e67c3b", fontWeight: 600, fontSize: "0.72rem", lineHeight: 1 }}>
                Outgoing
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#e65100", fontSize: "1rem", lineHeight: 1 }}>
              {summary.channelCommissions.count}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1 }}>
              {formatCurrency(summary.channelCommissions.total)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Calculation Breakdown & Action */}
      <Box
        sx={{
          p: 0.65,
          bgcolor: "#f5f5f5",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 0.5,
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.78rem" }}>
            Calc: {formatCurrency(summary.customerTopups.total)} − {formatCurrency(summary.inTransit.total)} − {formatCurrency(summary.channelCommissions.total)}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 13, color: "#ffffff" }} />}
          onClick={handleViewDetails}
          sx={{
            fontWeight: 700,
            textTransform: "none",
            whiteSpace: "nowrap",
            fontSize: "0.82rem",
            padding: "4px 12px",
            minHeight: "28px",
            bgcolor: "#00897b",
            color: "#ffffff",
            "&:hover": {
              bgcolor: "#00695c",
            },
          }}
        >
          View Details
        </Button>
      </Box>
    </Card>
  );
};

export default PendingApprovalsSimplified;
